import { useLazyQuery, WatchQueryFetchPolicy } from '@apollo/client'

import { CACHE_FIRST_FETCH_POLICY } from '@flowwai-frontend/bps-officer-web/graphql'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import { TemplateBaseDetailsType } from '../../types'
import {
   convertToErrorType,
   getAPIStatusFromNetworkStatus
} from '../../utils/APIUtils'
import CustomError from '../../models/CustomError'

import {
   GetPipelineItemTemplateDataQuery as Query,
   GetPipelineItemTemplateDataDocument as QueryDocument,
   GetPipelineItemTemplateDataQueryVariables as QueryVariables
} from './query.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   error?: Error | null
   data?: CustomError<Query['getSalesTemplates']>
}

interface TriggerAPIArgsType {
   templateIds: string[]

   fetchPolicy?: WatchQueryFetchPolicy
}

interface APIHookReturnType {
   apiError: Error | null
   apiStatus: APIStatus
   templates: Array<TemplateBaseDetailsType>

   triggerAPI: (args: TriggerAPIArgsType) => Promise<void>
}

interface Props {
   onFailure?: (args: FailureArgsType) => void
   onSuccess?: (templates: Array<TemplateBaseDetailsType>) => void
}

const useGetPipelineTemplateBaseDetails = (props: Props): APIHookReturnType => {
   const [getFields, { data, error, networkStatus }] = useLazyQuery<
      Query,
      QueryVariables
   >(QueryDocument)

   const { templates } = useAPIResponseHandler({
      data,
      onFailure: (errorMessage: string, data: Query['getSalesTemplates']) =>
         props.onFailure?.({
            data: new CustomError(errorMessage, data)
         }),
      onSuccess: props.onSuccess
   })

   const triggerAPI = async (args: TriggerAPIArgsType): Promise<void> => {
      const requestObj: QueryVariables = {
         params: {
            salesTemplateIds: args.templateIds
         }
      }

      await getFields({
         variables: requestObj,
         fetchPolicy: args.fetchPolicy ?? CACHE_FIRST_FETCH_POLICY,

         onError: (error: Error): void => props.onFailure?.({ error })
      })
   }

   const apiError = convertToErrorType(error)

   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,
      templates,

      triggerAPI
   }
}

export default useGetPipelineTemplateBaseDetails
